import { FC, useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { NumericFormat } from 'react-number-format';
import {
  Box,
  IconButton,
  Tooltip,
  useTheme,
  styled,
  Alert,
} from '@mui/material';
import { numberToCurrencyString, dateToLabel } from 'further-ui/utils';
import { useDisclosure } from 'further-ui/hooks';
import FieldRow from 'components/FormElements/FieldRow';
import {
  createClosedTrancheRoute,
  createInvestorViewRoute,
  createOpenTrancheRoute,
} from 'adminConstants/routes';
import AppTextInput from 'components/FormElements/AppTextInput';
import AdviserSelect, { defaultLabelRenderer } from 'components/AdviserSelect';
import InvestmentsFreeze from './InvestmentFreeze';
import InvestmentReassign from './InvestmentReassign';
import { CompareArrows, Add, Remove } from '@mui/icons-material';
import { sumBy } from 'lodash';
import TransferLabel from './TransferLabel';
import FundsReceivedStatusLabel from './FundsReceivedStatusLabel';
import { useDownloadByInvestmentId } from 'hooks/data/eisWizard/useDownloadEisCertificatePdfs';
import {
  Api,
  CustomTag,
  InvestmentCustomTagCategory,
} from 'further-types/investment';
import Spacing from 'components/Spacing';
import PageHeader from 'components/PageHeader';
import Heading from 'components/Heading';
import ResponsiveActions from 'components/PageHeader/ResponsiveActions';
import PageSection from 'components/PageSection';
import { Row } from 'components/Layout';
import AppSelectBox from 'components/FormElements/AppSelectBox';
import api from 'lib/trpcClient';
import CustomTagSection from 'components/CustomTagSelector';
import useStyles from '../styles';
import { FundStatus } from 'adminConstants/fundProperties';
import TrancheSelector from 'components/TrancheSelector';
import Button from 'components/Button';
import ReassignmentTooltip from './InvestmentReassign/ReassignmentTooltip';
import { AppDatePicker } from 'components/FormElements';
import { InvestmentCustomTagCategoryLabels } from 'further-ui/labels';
import { useCustomInvestmentTags } from 'hooks/data/investment/useCustomInvestmentTags';
import { useSubscriptionCodeWarning } from 'hooks/data/investment/useSubscriptionCodeWarning';
import { useHasPermissions } from 'hooks/ui/usePermissions';
import { RolePermission } from 'further-types/admin-user-role';

const StyledDatePickerWrapper = styled('div')(() => ({
  width: '100%',
  maxWidth: '300px',
}));

const tagCategoryOptions = Object.values(InvestmentCustomTagCategory).map(
  (category) => ({
    label: InvestmentCustomTagCategoryLabels[category],
    value: category,
  }),
);

export type EditableInvestmentDetails = {
  totalCommitment?: number;
  adviser?: {
    _id: string;
  };
  additionalAdviserIds?: Array<string>;
  adviserFee?: number;
  externalInvestmentId?: string;
  coreFeeTemplateId?: string;
  performanceFeeTemplateId?: string;
  customTags?: Array<CustomTag>;
  fund?: {
    _id: string;
  };
  investmentDate?: Date;
};

interface Props {
  shareholdings: Array<any>;
  investmentDetails?: Api.GetInvestmentDetailsResponse;
  paymentExpectations: Array<Api.InvestmentPaymentExpectation>;
  editableDetails: EditableInvestmentDetails;
  setEditableDetails: (details: EditableInvestmentDetails) => void;
  fees: Array<any>;
  investmentIsAllocated: boolean;
  hasExportedFees: boolean;
  calculatingNewFees: boolean;
  hasExits: boolean;
}

const AddAdditionalAdviserRow = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  width: '100%',
  '& > div': {
    width: '100%',
  },
}));

const InvestmentDetails: FC<Props> = ({
  shareholdings,
  investmentDetails,
  paymentExpectations,
  editableDetails,
  setEditableDetails,
  fees,
  investmentIsAllocated,
  hasExportedFees,
  calculatingNewFees,
  hasExits,
}) => {
  const { id } = useParams<{ id: string }>();
  const freezeDisclosure = useDisclosure();
  const reassignDisclosure = useDisclosure();
  const downloadEisCerts = useDownloadByInvestmentId();
  const theme = useTheme();
  const { classes } = useStyles();
  const [canEdit] = useHasPermissions([RolePermission.EditInvestment]);

  const handleDownload = async () =>
    downloadEisCerts.download({
      investmentId: id,
      fullName: fullName ?? '',
      trancheName: `${firmName}-${fundName}`,
    });

  const {
    investmentDate,
    totalCommitment,
    investmentAmount,
    adviserFeeExportedToCustodian,
    adviser,
    organisationUser,
    advisedClientCertifiedAt,
    commissionFee,
    isAllocationExists,
    hasFrozenFundsMoved,
    transferredInvestments = [],
    investmentBalance,
    sourceInvestmentId,
    firm,
    additionalAdviserIds,
    isKiFundReassignmentDisabled,
    originalInvestmentAmount,
  } = investmentDetails ?? {};

  const { data: customTags } = useCustomInvestmentTags({
    firmId: firm?._id,
    enabled: true,
  });
  const [customTagCategory, setCustomTagCategory] = useState<string>();

  const subscriptionCodeWarning = useSubscriptionCodeWarning({
    subscriptionCode: editableDetails.externalInvestmentId,
    firmId: firm?._id,
    investmentId: id,
  });

  const [
    additionalAdviserDropdownCounter,
    setAdditionalAdviserDropdownCounter,
  ] = useState<number>(1);

  useEffect(() => {
    if (additionalAdviserIds && additionalAdviserIds.length > 0) {
      setAdditionalAdviserDropdownCounter(additionalAdviserIds.length);
    }
  }, [additionalAdviserIds]);

  const investorId = investmentDetails?.investor?._id;
  const fullName = investmentDetails?.investor?.fullName;
  const firmName = investmentDetails?.firm?.firmName;
  const fundName = investmentDetails?.fund?.fundName;
  const fundId = investmentDetails?.fund?._id ?? '';
  const fundStatus = investmentDetails?.fund?.fundStatus;
  const frozenOn = investmentDetails?.freezeStartDate;
  const availableFunds =
    investmentBalance?.deploymentSummary?.heldByFundAwaitingInvestment ?? 0;
  const fundFullyDeployed = investmentDetails?.fund?.fullyDeployed;

  const isFrozen = frozenOn && new Date() >= new Date(frozenOn);

  const recentShareholdingDate =
    (shareholdings ?? [])
      .map((shareholding) => new Date(shareholding.date))
      .sort((date1, date2) => Number(date2) - Number(date1))[0] ?? null;

  const totalTransferredAmount = sumBy(
    transferredInvestments,
    'investmentAmount',
  );
  const fundsReceived = totalTransferredAmount + (investmentAmount ?? 0);

  const transferTooltipText = `This subscription was originally ${numberToCurrencyString(
    originalInvestmentAmount ?? fundsReceived,
  )}${
    originalInvestmentAmount && originalInvestmentAmount < fundsReceived
      ? `, but only ${numberToCurrencyString(fundsReceived)} was received`
      : ''
  }. ${numberToCurrencyString(
    totalTransferredAmount,
  )} has been transferred as set out on this page.`;

  const fundHref =
    fundStatus === FundStatus.CLOSED
      ? createClosedTrancheRoute(fundId)
      : createOpenTrancheRoute(fundId);

  const { data: coreFeeTemplates } = api.coreFeeTemplates.list.useQuery({
    firmId: investmentDetails?.firm?._id,
    order: 'desc',
    orderBy: 'createdAt',
  });

  const { data: performanceFeeTemplates } =
    api.performanceFeeTemplates.list.useQuery({
      investmentId: id,
      firmId: investmentDetails?.firm?._id,
      order: 'desc',
      orderBy: 'createdAt',
    });

  const isCoreFeePresetDisabled =
    investmentIsAllocated ||
    hasExportedFees ||
    coreFeeTemplates?.result?.length === 0 ||
    fundFullyDeployed;
  const isPerformanceFeePresetDisabled =
    performanceFeeTemplates?.result?.length === 0;

  const isTransferorInvestment = transferredInvestments.length > 0;
  const isTransfereeInvestment = !!sourceInvestmentId;

  const allowTrancheUpdate =
    !fundFullyDeployed &&
    !isTransfereeInvestment &&
    !isTransferorInvestment &&
    !isFrozen &&
    !investmentIsAllocated &&
    editableDetails.fund;

  const allowInvestmentReassignment =
    !isTransfereeInvestment &&
    !isTransferorInvestment &&
    !isFrozen &&
    !isKiFundReassignmentDisabled;

  return (
    <>
      <InvestmentsFreeze
        investmentId={id}
        frozenOn={frozenOn}
        freezeDisclosure={freezeDisclosure}
        fees={fees}
        hasFrozenFundsMoved={!!hasFrozenFundsMoved}
        availableFunds={availableFunds}
        recentShareholdingDate={recentShareholdingDate}
      />
      <InvestmentReassign
        investmentId={id}
        reassignDisclosure={reassignDisclosure}
        firmId={firm?._id}
        currentInvestorId={investorId}
      />

      {transferredInvestments.map((transferredInvestment) => (
        <TransferLabel
          key={transferredInvestment._id}
          investment={transferredInvestment}
        />
      ))}
      <Spacing>
        <PageSection withSpacing>
          <PageHeader>
            <Heading variant="h2" noMargin>
              Investor details
            </Heading>
            <ResponsiveActions>
              {!isFrozen && canEdit ? (
                <Button
                  size="small"
                  variant="outlined"
                  color="primary"
                  className="mr-2"
                  onClick={freezeDisclosure.onOpen}
                >
                  Freeze subscription
                </Button>
              ) : null}
              {!!isAllocationExists && (
                <Button
                  className="mr-2"
                  variant="outlined"
                  color="primary"
                  size="small"
                  onClick={handleDownload}
                >
                  Download all EIS certificates
                </Button>
              )}

              {canEdit && (
                <Button
                  className="mr-2"
                  variant="outlined"
                  color="primary"
                  size="small"
                  onClick={reassignDisclosure.onOpen}
                  disabled={!allowInvestmentReassignment}
                  tooltip={
                    !allowInvestmentReassignment ? (
                      <ReassignmentTooltip
                        allowInvestmentReassignment={
                          allowInvestmentReassignment
                        }
                        isKiFundReassignmentDisabled={
                          !!isKiFundReassignmentDisabled
                        }
                        isFrozen={!!isFrozen}
                        isTransfereeInvestment={isTransfereeInvestment}
                        isTransferorInvestment={isTransferorInvestment}
                      />
                    ) : undefined
                  }
                >
                  Reassign subscription
                </Button>
              )}

              <Link to={createInvestorViewRoute(investorId ?? '')}>
                <Button size="small" variant="outlined" color="primary">
                  View investor
                </Button>
              </Link>
            </ResponsiveActions>
          </PageHeader>
          <FieldRow title="Investor name" centerTitle>
            {fullName}
          </FieldRow>
          <FieldRow title="Firm" centerTitle>
            {firmName}
          </FieldRow>
          <FieldRow title="Fund" centerTitle>
            {allowTrancheUpdate && canEdit ? (
              <TrancheSelector
                maxWidth={300}
                onChange={(trancheOption) =>
                  setEditableDetails({
                    ...editableDetails,
                    fund: trancheOption ?? undefined,
                  })
                }
                firmId={investmentDetails?.firm._id}
                selectedTranche={editableDetails.fund}
              />
            ) : (
              <Link className={classes.link} to={fundHref}>
                {fundName}
              </Link>
            )}
          </FieldRow>
          <FieldRow title="Subscription date" centerTitle>
            <StyledDatePickerWrapper>
              <AppDatePicker
                required
                name="investmentDate"
                value={
                  editableDetails.investmentDate ||
                  (investmentDate ? new Date(investmentDate) : undefined)
                }
                onChange={(date) => {
                  setEditableDetails({
                    ...editableDetails,
                    investmentDate: date ?? undefined,
                  });
                }}
                disabled={!canEdit}
              />
            </StyledDatePickerWrapper>
          </FieldRow>
        </PageSection>

        <PageSection withSpacing showDivider>
          <FieldRow
            title="Total commitment"
            centerTitle
            tooltipText={
              fundFullyDeployed
                ? 'The total commitment cannot be amended as the fund is now fully deployed.'
                : undefined
            }
          >
            {totalTransferredAmount > 0 ? (
              <Box alignItems="center" display="flex" gap={4}>
                {numberToCurrencyString(totalCommitment)}
                <Tooltip title={transferTooltipText}>
                  <CompareArrows />
                </Tooltip>
              </Box>
            ) : (
              <NumericFormat
                required
                prefix="£"
                decimalScale={2}
                fixedDecimalScale
                customInput={AppTextInput}
                name="totalCommitment"
                placeholder=""
                allowNegative={false}
                allowLeadingZeros={false}
                thousandSeparator={true}
                disabled={fundFullyDeployed || calculatingNewFees || !canEdit}
                onValueChange={({ value }) =>
                  setEditableDetails({
                    ...editableDetails,
                    totalCommitment: value ? Number(value) : undefined,
                  })
                }
                onBlur={() => {
                  if (!editableDetails.totalCommitment) {
                    setEditableDetails({
                      ...editableDetails,
                      totalCommitment: 0,
                    });
                  }
                }}
                style={{ maxWidth: 300 }}
                value={editableDetails.totalCommitment ?? undefined}
                variant="outlined"
              />
            )}
          </FieldRow>
          <FieldRow title="Funds received" centerTitle>
            <Row centered>
              {numberToCurrencyString(
                investmentBalance?.deploymentSummary?.fundsReceived,
              )}
              <FundsReceivedStatusLabel
                paymentExpectations={paymentExpectations}
                fundsReceived={
                  investmentBalance?.deploymentSummary?.fundsReceived ?? 0
                }
                isTransferorInvestment={isTransferorInvestment}
                isTransfereeInvestment={isTransfereeInvestment}
              />
            </Row>
          </FieldRow>
          <FieldRow
            title="Advice fee"
            centerTitle
            tooltipText={
              fundFullyDeployed
                ? 'The advice fee cannot be amended as the fund is now fully deployed.'
                : adviserFeeExportedToCustodian
                ? 'The advice fee cannot be amended as the fee has been exported to a custodian.'
                : undefined
            }
          >
            <NumericFormat
              required
              prefix="£"
              decimalScale={2}
              fixedDecimalScale
              customInput={AppTextInput}
              name="adviserFee"
              placeholder=""
              allowNegative={false}
              allowLeadingZeros={false}
              thousandSeparator={true}
              disabled={
                fundFullyDeployed ||
                calculatingNewFees ||
                adviserFeeExportedToCustodian ||
                !canEdit
              }
              onValueChange={({ value }) =>
                setEditableDetails({
                  ...editableDetails,
                  adviserFee: value ? Number(value) : undefined,
                })
              }
              onBlur={() => {
                if (!editableDetails.adviserFee) {
                  setEditableDetails({
                    ...editableDetails,
                    adviserFee: 0,
                  });
                }
              }}
              style={{ maxWidth: 300 }}
              value={editableDetails.adviserFee ?? undefined}
              variant="outlined"
            />
          </FieldRow>
          <FieldRow title="Commission" centerTitle>
            {numberToCurrencyString(commissionFee || 0)}
          </FieldRow>

          <FieldRow
            title="Core fee preset"
            centerTitle
            tooltipText={
              <>
                Changing the core fee template will update all fees on this
                subscription.
                <br />
                <br />
                This can only be done before any allocations have been made and
                before any fees are exported to custodians.
              </>
            }
          >
            <AppSelectBox
              data={[
                {
                  _id: '',
                  name: 'No fees',
                },
                ...(coreFeeTemplates?.result || []),
              ]}
              valueKey="_id"
              labelKey="name"
              value={editableDetails.coreFeeTemplateId}
              onChange={(event) =>
                setEditableDetails({
                  ...editableDetails,
                  coreFeeTemplateId: event.target.value as string,
                })
              }
              disabled={isCoreFeePresetDisabled || !canEdit}
              backgroundColor={
                isCoreFeePresetDisabled ? theme.palette.grey[100] : undefined
              }
              maxWidth={300}
            />
          </FieldRow>
          <FieldRow
            title="Performance fee preset"
            centerTitle
            tooltipText={
              <>
                Changing the performance fee template will update this
                subscriptions performance fee settings.
                <br />
                <br />
                This can be changed to any performance fee before the first
                exit. After the first exit, it can be changed to any performance
                fee that shares the same hurdle basis and gross/net settings.
                <br />
                <br />
                Changing the performance fee setting will not affect previously
                charged performance fees, but will affect future performance fee
                calculations.
              </>
            }
          >
            <AppSelectBox
              data={[
                // Prevent displaying No fees option if there are exits unless already selected
                ...(!hasExits || !editableDetails.performanceFeeTemplateId
                  ? [
                      {
                        _id: '',
                        name: 'No fees',
                      },
                    ]
                  : []),
                ...(performanceFeeTemplates?.result || []),
              ]}
              valueKey="_id"
              labelKey="name"
              value={editableDetails.performanceFeeTemplateId}
              onChange={(event) =>
                setEditableDetails({
                  ...editableDetails,
                  performanceFeeTemplateId: event.target.value as string,
                })
              }
              disabled={isPerformanceFeePresetDisabled || !canEdit}
              backgroundColor={
                isPerformanceFeePresetDisabled
                  ? theme.palette.grey[100]
                  : undefined
              }
              maxWidth={300}
            />
          </FieldRow>
        </PageSection>
        <PageSection withSpacing showDivider>
          <FieldRow title="Adviser" centerTitle>
            <AdviserSelect
              adviserId={editableDetails.adviser?._id}
              onChange={(adviser) => {
                if (!adviser) {
                  setAdditionalAdviserDropdownCounter(1);
                  setEditableDetails({
                    ...editableDetails,
                    adviser: undefined,
                    additionalAdviserIds: [],
                  });
                } else {
                  setEditableDetails({
                    ...editableDetails,
                    adviser: adviser ?? undefined,
                  });
                }
              }}
              disabled={!canEdit}
              hiddenAdviserIdsFromOptions={[
                ...(editableDetails.additionalAdviserIds ?? []),
              ]}
              placeholder="N/A - Direct subscription"
              labelRenderer={(option) =>
                option ? defaultLabelRenderer(option) : 'Direct'
              }
              id="adviserId"
            />
          </FieldRow>
          <FieldRow title="Advised client suitability assessment" centerTitle>
            {advisedClientCertifiedAt ? (
              <span>
                Confirmed by{' '}
                {organisationUser?.fullName
                  ? `${organisationUser?.fullName} on behalf of ${adviser?.fullName}`
                  : adviser?.fullName}{' '}
                - {dateToLabel(advisedClientCertifiedAt, '-', true)}
              </span>
            ) : adviser || editableDetails.adviser ? (
              'N/A'
            ) : (
              'N/A - Direct subscription'
            )}
          </FieldRow>

          <FieldRow title="Additional linked adviser(s)" labelSpacing={10}>
            <Spacing>
              {Array.from({
                length: additionalAdviserDropdownCounter ?? 0,
              }).map((_, index) => (
                <AddAdditionalAdviserRow
                  key={editableDetails.additionalAdviserIds?.[index] ?? index}
                >
                  <AdviserSelect
                    adviserId={editableDetails.additionalAdviserIds?.[index]}
                    hiddenAdviserIdsFromOptions={[
                      ...(editableDetails.additionalAdviserIds ?? []),
                      ...(editableDetails.adviser?._id
                        ? [editableDetails.adviser._id]
                        : []),
                    ]}
                    disabled={!editableDetails.adviser?._id || !canEdit}
                    onChange={(adviser) => {
                      if (!adviser) {
                        setEditableDetails({
                          ...editableDetails,
                          additionalAdviserIds:
                            editableDetails.additionalAdviserIds?.filter(
                              (id) =>
                                id !==
                                editableDetails.additionalAdviserIds?.[index],
                            ),
                        });
                      } else {
                        const newAdditionalAdviserIds = [
                          ...(editableDetails.additionalAdviserIds ?? []),
                        ];
                        newAdditionalAdviserIds[index] = adviser._id;
                        setEditableDetails({
                          ...editableDetails,
                          additionalAdviserIds: newAdditionalAdviserIds,
                        });
                      }
                    }}
                    labelRenderer={(option) =>
                      option ? defaultLabelRenderer(option) : 'Direct'
                    }
                  />
                  <IconButton
                    color="primary"
                    disabled={
                      (index === 0 && additionalAdviserDropdownCounter === 1) ||
                      !canEdit
                    }
                    onClick={() => {
                      if (additionalAdviserDropdownCounter === 1) return;
                      const newAdditionalAdviserIds = [
                        ...(editableDetails.additionalAdviserIds ?? []),
                      ];
                      newAdditionalAdviserIds.splice(index, 1);
                      setEditableDetails({
                        ...editableDetails,
                        additionalAdviserIds: newAdditionalAdviserIds,
                      });
                      setAdditionalAdviserDropdownCounter(
                        additionalAdviserDropdownCounter - 1,
                      );
                    }}
                  >
                    <Remove />
                  </IconButton>
                  {index === additionalAdviserDropdownCounter - 1 && (
                    <IconButton
                      color="primary"
                      disabled={!editableDetails.adviser?._id || !canEdit}
                      onClick={() => {
                        if (!editableDetails.adviser?._id) return;
                        setAdditionalAdviserDropdownCounter(
                          additionalAdviserDropdownCounter + 1,
                        );
                      }}
                    >
                      <Add />
                    </IconButton>
                  )}
                </AddAdditionalAdviserRow>
              ))}
            </Spacing>
          </FieldRow>

          <FieldRow title="External subscription ID" centerTitle>
            <>
              <AppTextInput
                name="externalInvestmentId"
                variant="outlined"
                style={{ maxWidth: 300 }}
                value={editableDetails.externalInvestmentId}
                onChange={(event) =>
                  setEditableDetails({
                    ...editableDetails,
                    externalInvestmentId: event.target.value,
                  })
                }
                disabled={!canEdit}
              />
              {subscriptionCodeWarning.hasWarning && (
                <Alert severity="warning" sx={{ mt: 5 }}>
                  {subscriptionCodeWarning.message}
                </Alert>
              )}
            </>
          </FieldRow>

          <FieldRow title="Custom tag(s)" centerTitle>
            <CustomTagSection
              disabled={!canEdit}
              onChange={(tags) => {
                setEditableDetails({
                  ...editableDetails,
                  customTags: tags as Array<CustomTag>,
                });
                setCustomTagCategory(undefined);
              }}
              selectedCategory={customTagCategory}
              onCategorySelect={(category) => {
                setCustomTagCategory(category);
              }}
              categoryOptions={tagCategoryOptions}
              tagOptions={customTags?.[customTagCategory ?? '']}
              tags={editableDetails.customTags ?? []}
            />
          </FieldRow>
          {investmentDetails?.createdByUserType &&
            investmentDetails?.createdAt && (
              <FieldRow title="Subscription created by" centerTitle>
                {investmentDetails.createdByUserType} -{' '}
                {dateToLabel(investmentDetails.createdAt, '-', true)}
              </FieldRow>
            )}
        </PageSection>
      </Spacing>
    </>
  );
};

export default InvestmentDetails;
